import type {
  Category,
  DealAnalysis,
  DealItem,
  DiscountedProduct,
  Paginated,
  PriceHistoryPoint,
  PriceDropItem,
  ProductDetail,
  ProductListItem,
  Store,
  WatchlistItem,
} from './types';

// En dev queda vacio y usa el proxy de Vite; en produccion se define
// VITE_API_BASE_URL (ej. https://api.tudominio.com) en las env vars del build.
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}/api${path}`);
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText} on ${path}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  stores: () => get<Store[]>('/stores'),

  products: (params: {
    search?: string;
    store?: string;
    brand?: string;
    minPrice?: number;
    maxPrice?: number;
    sort?: 'newest' | 'price_asc' | 'price_desc' | 'discount';
    limit?: number;
    offset?: number;
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.search)              qs.set('search',   params.search);
    if (params.store)               qs.set('store',    params.store);
    if (params.brand)               qs.set('brand',    params.brand);
    if (params.minPrice != null)    qs.set('minPrice', String(params.minPrice));
    if (params.maxPrice != null)    qs.set('maxPrice', String(params.maxPrice));
    if (params.sort && params.sort !== 'newest') qs.set('sort', params.sort);
    if (params.limit != null)       qs.set('limit',    String(params.limit));
    if (params.offset != null)      qs.set('offset',   String(params.offset));
    const q = qs.toString();
    return get<Paginated<ProductListItem>>(`/products${q ? `?${q}` : ''}`);
  },

  product: (id: string) => get<ProductDetail>(`/products/${id}`),

  history: (id: string, days = 90) =>
    get<PriceHistoryPoint[]>(`/products/${id}/history?days=${days}`),

  analysis: (id: string) => get<DealAnalysis>(`/products/${id}/analysis`),

  saveNotes: (id: string, notes: string) =>
    fetch(`${API_BASE}/api/products/${id}/notes`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes }),
    }).then((r) => {
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      return r.json() as Promise<{ id: string; notes: string | null }>;
    }),

  categories: () => get<Category[]>('/categories'),

  discounts: (params: {
    category?: string;
    sort?: 'discount' | 'newest' | 'price_asc' | 'price_desc';
    limit?: number;
    offset?: number;
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.category)        qs.set('category', params.category);
    if (params.sort && params.sort !== 'discount') qs.set('sort', params.sort);
    if (params.limit != null)   qs.set('limit',    String(params.limit));
    if (params.offset != null)  qs.set('offset',   String(params.offset));
    const q = qs.toString();
    return get<Paginated<DiscountedProduct>>(`/discounts${q ? `?${q}` : ''}`);
  },

  deals: (params: { minScore?: number; store?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.minScore != null) qs.set('minScore', String(params.minScore));
    if (params.store) qs.set('store', params.store);
    if (params.limit != null) qs.set('limit', String(params.limit));
    const q = qs.toString();
    return get<DealItem[]>(`/deals${q ? `?${q}` : ''}`);
  },

  priceDrops: (params: { since?: number; minDropPct?: number; limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.since != null)      qs.set('since',      String(params.since));
    if (params.minDropPct != null) qs.set('minDropPct', String(params.minDropPct));
    if (params.limit != null)      qs.set('limit',      String(params.limit));
    if (params.offset != null)     qs.set('offset',     String(params.offset));
    const q = qs.toString();
    return get<Paginated<PriceDropItem>>(`/price-drops${q ? `?${q}` : ''}`);
  },

  watchlist: (userId: string) =>
    get<WatchlistItem[]>(`/watchlists/${encodeURIComponent(userId)}`),

  addWatchlist: (body: {
    userId: string;
    productId: string;
    dealScoreThreshold?: number;
    hardPriceCents?: number;
  }) =>
    fetch(`${API_BASE}/api/watchlists`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => {
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      return r.json() as Promise<{ id: string }>;
    }),

  removeWatchlist: (id: string) =>
    fetch(`${API_BASE}/api/watchlists/${encodeURIComponent(id)}`, { method: 'DELETE' }).then((r) => {
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    }),
};
